import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlatformUser } from '@/types/platform-auth.types';

interface PlatformAuthState {
  user: PlatformUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  /** Full login — stores all three values. Used only by the login page. */
  setAuth: (user: PlatformUser, accessToken: string, refreshToken: string) => void;
  /** Validates a stored session on startup — updates user only, keeps existing tokens. */
  setInitialUser: (user: PlatformUser) => void;
  /** Swaps both tokens after a successful refresh rotation. Does not touch the user. */
  rotateTokens: (accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  setInitialized: () => void;
}

export const usePlatformAuthStore = create<PlatformAuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isInitialized: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true }),

      setInitialUser: (user) =>
        set({ user, isAuthenticated: true }),

      rotateTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      clearAuth: () =>
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),

      setInitialized: () => set({ isInitialized: true }),
    }),
    {
      name: 'platform-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // isInitialized intentionally excluded — always re-validate on startup
      }),
    },
  ),
);
